import { WEB } from '@/constants/platforms';
import routesDefinition from '../../routes/routes';
import roleConstants from '../../utilities/enums/rolesTypes';

const defaultState = () => ({
    // to check if state Access Control is loaded
    isStateAccessControlLoaded: false,
    // Merged permissions of all the different roles of the current user member related to the current selected team
    permissions: {},
    // Roles list of the current user member related to the current selected team
    roles: [],
    // Map with all the routes accessible for the user based on his role/permissions, key are route path here, value is route object
    accessibleRoutesByPath: {},
    // Map with all the routes accessible for the user based on his role/permissions, key are component name and value route object
    accessibleRoutesByName: {},
    // to check if we display the release note or not and terms or not
    displayNote: false,
    // to check if we display the terms or not
    termsOfUseAccepted: true,
    // to check if user is superAdmin
    superAdmin: false,
});

const mutations = {
    setIsStateAccessControlLoaded(state, { isStateAccessControlLoaded }) {
        state.isStateAccessControlLoaded = isStateAccessControlLoaded;
    },
    setPermissions(state, { permissions }) {
        state.permissions = permissions;
    },
    setRoles(state, { roles }) {
        state.roles = roles;
    },
    setTermsOfUseAccepted(state, { termsOfUseAccepted }) {
        state.termsOfUseAccepted = termsOfUseAccepted;
    },
    setDisplayNote(state, { displayNote }) {
        state.displayNote = displayNote;
    },
    setSuperAdmin(state, { superAdmin }) {
        state.superAdmin = superAdmin;
    },
    setAccessibleRoutesByPath(state, { accessibleRoutesByPath }) {
        state.accessibleRoutesByPath = JSON.parse(JSON.stringify(accessibleRoutesByPath));
    },
    setAccessibleRoutesByName(state, { accessibleRoutesByName }) {
        state.accessibleRoutesByName = JSON.parse(JSON.stringify(accessibleRoutesByName));
    },
    resetAccessControlState(state) {
        Object.assign(state, defaultState());
    },
};

// HELPER FUNCTIONS
const buildPermissionsWithoutDuplicates = roles => {
    const mergedPermissionsWithDuplicates = roles.map(role => role.permissions).reduce((a, b) => [...a, ...b], []);
    return (Array.from(new Set(mergedPermissionsWithDuplicates.map(perm => perm.key))))
        .reduce((o, key) => Object.assign(o, { [key.split('.').join('_')]: key }), {});
};

const buildAccessibleRoutes = (routes, context) => {
    const childrenRoutesWithParentRoutes = routes.reduce((output, route) => {
        if (Array.isArray(route.children)) {
            output.push(...route.children.map(child => ({
                alias: child.alias,
                meta: child.meta,
                name: child.name,
                path: `${route.path}/${child.path}`,
            })));
        }

        output.push({
            alias: route.alias,
            meta: route.meta,
            name: route.name,
            path: route.path,
        });

        return output;
    }, []);

    return childrenRoutesWithParentRoutes.filter(route => {
        if (route.path === '*') return false;
        if (!route.meta) return true;
        if (route.meta.everyNeededPermissions && !context.getters.hasEveryPermissions(route.meta.everyNeededPermissions)) return false;
        return !(route.meta.atLeastNeededRoles && !context.getters.hasAtLeastOneRole(route.meta.atLeastNeededRoles));
    });
};

const actions = {
    async setAccessControlInfo(context) {
        try {
            const response = await this.$rq.getMemberInfo('me');
            const { id, roles, superAdmin } = response.data;
            // Mutation in auth module
            context.commit('setCurrentMember', { id, roles });
            context.commit('setSuperAdmin', { superAdmin });

            // Mutation in alertsNotification
            context.commit('startInterval');
            context.commit({
                type: 'setTermsOfUseAccepted',
                termsOfUseAccepted: response.data.termsOfUseAccepted,
            });

            context.commit({
                type: 'setTermsOfUseAccepted',
                termsOfUseAccepted: response.data?.termsOfUseAccepted || false,
            });

            context.commit({
                type: 'setDisplayNote',
                displayNote: (response.data?.displayNote?.[WEB]) || false,
            });


            context.commit({
                type: 'setRoles',
                roles: roles.reduce((o, role) => Object.assign(o, { [role.key.split('.').join('_')]: role }), {}),
            });

            context.commit('setLang', context.getters.user.lang);

            context.commit({
                type: 'setPermissions',
                permissions: buildPermissionsWithoutDuplicates(roles),
            });
            const accessibleRoutes = buildAccessibleRoutes(Object.assign([], routesDefinition), context);
            context.commit({
                type: 'setAccessibleRoutesByPath',
                accessibleRoutesByPath: JSON.parse(JSON.stringify(accessibleRoutes))
                    .filter(route => route.path)
                    .reduce((o, route) => Object.assign(o, { [route.path.split('.').join('_')]: route }, {})),
            });
            context.commit({
                type: 'setAccessibleRoutesByName',
                accessibleRoutesByName: JSON.parse(JSON.stringify(accessibleRoutes))
                    .filter(route => route.name)
                    .reduce((o, route) => Object.assign(o, { [route.name.split('.').join('_')]: route }, {})),
            });
            context.commit({
                type: 'setIsStateAccessControlLoaded',
                isStateAccessControlLoaded: true,
            });
        } catch (err) {
            context.dispatch('resetAccessControlState');
            context.dispatch('refreshUserTeams', { refreshToDefaultRoute: true }, { root: true });
        }
    },
    resetAccessControlState({ commit }) {
        commit({
            type: 'resetAccessControlState',
        });
    },
};

const getters = {
    isStateAccessControlLoaded: state => state.isStateAccessControlLoaded,
    roles: state => state.roles,
    superAdmin: state => state.superAdmin,
    displayNote: state => state.displayNote,
    termsOfUseAccepted: state => state.termsOfUseAccepted,
    permissions: state => state.permissions,
    accessibleRoutes: state => state.accessibleRoutes,
    hasPermission(state) {
        return permission => {
            let permRegexString;
            const hasPermission = permission
                .split('.')
                .some((part, index) => {
                    permRegexString = index === 0 ? part : `${permRegexString}_${part}`;
                    return state.permissions[`${permRegexString}_*`] !== undefined;
                });
            return hasPermission || state.permissions[permission.split('.').join('_')] !== undefined;
        };
    },
    hasRole(state) {
        return role => state.roles[role] !== undefined;
    },
    hasAtLeastOneRole(state, moduleGetters) {
        return roles => roles.some(role => moduleGetters.hasRole(role));
    },
    hasEveryPermissions(state, moduleGetters) {
        return permissions => permissions.every(permission => moduleGetters.hasPermission(permission));
    },
    hasAccessToRoute(state) {
        return route => {
            if (!route.name && typeof route !== 'string') return true;
            if (route === '/' || route === 'login' || route.name === 'login') return true;
            return (typeof route === 'string')
                ? state.accessibleRoutesByPath[route.split('.').join('_')] !== undefined || state.accessibleRoutesByName[route.split('.').join('_')] !== undefined
                : state.accessibleRoutesByPath[route.name.split('.').join('_')] !== undefined || state.accessibleRoutesByName[route.name.split('.').join('_')] !== undefined;
        };
    },
    isAdminOrManager() {
        return member => {
            const adminsAndManagers = [
                roleConstants.ADMINISTRATOR,
                roleConstants.MANAGER,
                roleConstants.LABMANAGER,
                roleConstants.LABMICROBIOLOGIST,
            ];
            return member.roles.find(role => adminsAndManagers.includes(role.key));
        };
    },
};

const state = defaultState();

export default {
    state,
    getters,
    actions,
    mutations,
};
